import React, { useState, useMemo } from 'react';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import { Modal } from 'react-bootstrap';
import FittedDistributionChart from './FittedDistributionChart';
import MapaIndicador from './MapaIndicador';

const AnalysisTable = ({ data, type, distribuicoes, codigoProjeto, mapa }) => {
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [showModal, setShowModal] = useState(false);
    const [selectedTransition, setSelectedTransition] = useState(null);

    // Transform split-format data to array of rows
    const tableData = useMemo(() => {
        if (!data || Object.keys(data).length === 0) return [];

        // Check if data is in 'split' format (pandas orient='split')
        if (data.columns && data.data && Array.isArray(data.columns) && Array.isArray(data.data)) {
            // Split format: {columns: [...], data: [[...], [...]], index: [...]}
            const columns = data.columns;
            const rows = data.data;

            return rows.map((rowData, rowIndex) => {
                const row = { _index: data.index ? data.index[rowIndex] : rowIndex };
                columns.forEach((colName, colIndex) => {
                    row[colName] = rowData[colIndex];
                });
                return row;
            });
        } else {
            // Legacy format: {col1: {0: val1, 1: val2}, col2: {0: val3, 1: val4}}
            const columnNames = Object.keys(data);
            if (columnNames.length === 0) return [];

            const firstColumnData = data[columnNames[0]];
            if (!firstColumnData || typeof firstColumnData !== 'object') return [];

            const rowIndices = Object.keys(firstColumnData);

            return rowIndices.map(rowIndex => {
                const row = { _index: rowIndex };
                columnNames.forEach(colName => {
                    row[colName] = data[colName][rowIndex];
                });
                return row;
            });
        }
    }, [data]);

    // Get column definitions
    const columns = useMemo(() => {
        if (!data || Object.keys(data).length === 0) return [];

        // Check if data is in 'split' format
        if (data.columns && Array.isArray(data.columns)) {
            // Split format: use columns array to preserve order
            return data.columns.map(key => ({
                key,
                label: key.replace(/_/g, ' ').toUpperCase(),
                sortable: true
            }));
        } else {
            // Legacy format: use object keys
            return Object.keys(data).map(key => ({
                key,
                label: key.replace(/_/g, ' ').toUpperCase(),
                sortable: true
            }));
        }
    }, [data]);

    // Sort data
    const sortedData = useMemo(() => {
        if (!sortConfig.key) return tableData;

        return [...tableData].sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            // Handle null/undefined values
            if (aVal === null || aVal === undefined) aVal = '';
            if (bVal === null || bVal === undefined) bVal = '';

            // Handle numeric values
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }

            // Handle string values
            aVal = String(aVal).toLowerCase();
            bVal = String(bVal).toLowerCase();

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [tableData, sortConfig]);

    // Paginate data
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(sortedData.length / itemsPerPage);

    const handleSort = (key) => {
        setSortConfig(prevConfig => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleTransicaoClick = (transicao, nivel) => {
        if (!transicao || !distribuicoes || !distribuicoes.distAjustadas) return;

        // Formatar a chave da transição no formato esperado: "TRL 5→6" ou "CRL 4→5"
        const transitionKey = `${nivel.toUpperCase()} ${transicao}`;

        // Verificar se existe distribuição para esta transição
        if (distribuicoes.distAjustadas[transitionKey]) {
            setSelectedTransition({
                key: transitionKey,
                nivel: nivel.toUpperCase(),
                projeto: codigoProjeto
            });
            setShowModal(true);
        }
    };

    const formatValue = (value) => {
        if (value === null || value === undefined) {
            return '-';
        }

        // Format numbers with appropriate precision
        if (typeof value === 'number') {
            // Check if it's likely a timestamp (large number)
            if (value > 1000000000000) {
                return new Date(value).toLocaleDateString('pt-BR');
            }
            // Regular number formatting
            return Number.isInteger(value) ? value.toString() : value.toFixed(4);
        }

        return String(value);
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <span className="text-gray-400">↕</span>;
        }
        return sortConfig.direction === 'asc' ?
            <span className="text-blue-600">↑</span> :
            <span className="text-blue-600">↓</span>;
    };

    if (columns.length === 0) {
        return (
            <div className="p-6 text-center text-gray-500">
                <p>Nenhum dado disponível</p>
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            {/* Table Container */}
            <div className="overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {columns.map((column) => (
                                    <th
                                        key={column.key}
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                                        onClick={() => column.sortable && handleSort(column.key)}
                                    >
                                        <div className="flex items-center space-x-1">
                                            <span>{column.label}</span>
                                            {column.sortable && getSortIcon(column.key)}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedData.map((row, rowIndex) => {
                                // Verificar se esta linha tem uma transição com distribuição
                                const transicaoValue = row['transicao'];
                                const transitionKey = `${type.toUpperCase()} ${transicaoValue}`;
                                const hasDistribution = transicaoValue &&
                                    distribuicoes &&
                                    distribuicoes.distAjustadas &&
                                    distribuicoes.distAjustadas[transitionKey];

                                return (
                                    <tr
                                        key={row._index || rowIndex}
                                        className="hover:bg-gray-50 transition-colors duration-150"
                                        onClick={() => {
                                            if (hasDistribution) {
                                                handleTransicaoClick(transicaoValue, type);
                                            }
                                        }}
                                        style={hasDistribution ? { cursor: 'pointer' } : {}}
                                    >
                                        {columns.map((column) => (
                                            <td
                                                key={column.key}
                                                className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                                            >
                                                {formatValue(row[column.key])}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-700">
                            Mostrando {(currentPage - 1) * itemsPerPage + 1} a{' '}
                            {Math.min(currentPage * itemsPerPage, sortedData.length)} de{' '}
                            {sortedData.length} registros
                        </span>

                        <select
                            value={itemsPerPage}
                            onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                        >
                            <option value={10}>10 por página</option>
                            <option value={25}>25 por página</option>
                            <option value={50}>50 por página</option>
                            <option value={100}>100 por página</option>
                        </select>
                    </div>

                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <HiChevronLeft className="w-5 h-5" />
                        </button>

                        <span className="text-sm text-gray-700">
                            Página {currentPage} de {totalPages}
                        </span>

                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <HiChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            )}

            {/* Modal para exibir distribuição ajustada */}
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="xl"
                centered
            >
                <Modal.Header closeButton className='bg-gradient-to-r from-slate-700 to-slate-600 text-white px-6 py-4 flex items-center' style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    padding: '12px 20px',
                    borderRadius: '8px 8px 0 0',
                    marginBottom: '0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    <Modal.Title>
                        {selectedTransition && `${selectedTransition.projeto} - Distribuição Ajustada - ${selectedTransition.key}`}
                    </Modal.Title>
                </Modal.Header>


                <Modal.Body>
                    {selectedTransition && distribuicoes && distribuicoes.distAjustadas && (
                        <div className="flex flex-col">
                            {/* Gráfico da distribuição */}
                            <div>
                                <FittedDistributionChart
                                    transitionKey={selectedTransition.key}
                                    distData={distribuicoes.distAjustadas[selectedTransition.key]}
                                    empiricalValues={
                                        distribuicoes.distEmpiricas?.dados?.[selectedTransition.key]
                                    }
                                    showTitle={false}
                                />
                            </div>

                            {/* Indicador de mapa - abaixo do gráfico */}
                            <div className="px-4 pb-2">
                                <MapaIndicador
                                    valor={mapa && mapa[selectedTransition.key] ? mapa[selectedTransition.key] : 0}
                                />
                            </div>
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default AnalysisTable;